import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './Projects.css';
import PageHero from './PageHero';
import { IconArrowRight } from './Icons';

const PROJECT_SECTIONS = [
  { key: 'web', label: 'Web & Systems' },
  { key: 'graphics', label: 'Graphics' },
  { key: 'video', label: 'Video Editing' },
];

/* -------------------------------------------------------------------------
   Shared pieces
   ------------------------------------------------------------------------- */

const TechStack = ({ items }) => (
  <ul className="tech-stack">
    {items.map((item) => (
      <li key={item.src}>
        <img src={item.src} alt={item.alt} title={item.title} />
        <span className="tech-stack-name">{item.alt}</span>
      </li>
    ))}
  </ul>
);

// Placeholder that closes out each tab without pretending to be a project.
const MoreToComeCard = () => (
  <article className="project-card project-card--placeholder reveal reveal-fade">
    <div className="more-to-come">
      <span className="more-to-come-mark" aria-hidden="true" />
      <h3>More to come</h3>
      <p>
        We&rsquo;re always building. New work lands here as it ships — check the
        Live Apps page for everything currently in production.
      </p>
      <Link to="/live-apps" className="btn btn--link">
        See what&rsquo;s live <span className="btn-arrow">→</span>
      </Link>
    </div>
  </article>
);

/* -------------------------------------------------------------------------
   Carousel (shared by the graphics and video tabs)
   ------------------------------------------------------------------------- */

const CarouselControls = ({ items, current, onSelect, label }) => (
  <div className="carousel-controls">
    <button
      type="button"
      className="carousel-arrow"
      onClick={() => onSelect((current - 1 + items.length) % items.length)}
      aria-label={`Previous ${label}`}
    >
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M15 5l-7 7 7 7"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>

    <div className="carousel-thumbnails" role="tablist" aria-label={label}>
      {items.map((item, idx) => (
        <button
          type="button"
          key={item.src}
          role="tab"
          aria-selected={idx === current}
          className={`carousel-thumb${idx === current ? ' active' : ''}`}
          onClick={() => onSelect(idx)}
        >
          <span className="carousel-thumb-index">{String(idx + 1).padStart(2, '0')}</span>
          {item.title}
        </button>
      ))}
    </div>

    <button
      type="button"
      className="carousel-arrow"
      onClick={() => onSelect((current + 1) % items.length)}
      aria-label={`Next ${label}`}
    >
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M9 5l7 7-7 7"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  </div>
);

/* -------------------------------------------------------------------------
   Page
   ------------------------------------------------------------------------- */

const Projects = () => {
  const [section, setSection] = useState('web');
  const [highlightStyle, setHighlightStyle] = useState({ left: 0, width: 0 });
  const miniNavRef = useRef(null);

  // Slide the segmented-control indicator under the active tab.
  useEffect(() => {
    if (!miniNavRef.current) return;
    const activeBtn = miniNavRef.current.querySelector('.mini-navbar-tab.active');
    if (activeBtn) {
      setHighlightStyle({ left: activeBtn.offsetLeft, width: activeBtn.offsetWidth });
    }
  }, [section]);

  return (
    <>
      <PageHero
        eyebrow="Work / Projects"
        title="Software, systems, and media built for real organizations."
        lede="Full-stack platforms, information management tools, marketing sites, brand identity, and post-production — grouped by discipline."
        meta={['Web & systems', 'Graphics', 'Video editing']}
      >
        <Link to="/live-apps" className="btn btn--primary">
          See live apps <span className="btn-arrow">→</span>
        </Link>
        <Link to="/products" className="btn btn--ghost">
          Products &amp; pricing
        </Link>
      </PageHero>

      <main className="main-content" id="main">
        <section className="section projects-section">
          <div className="container">
            {/* Segmented control */}
            <div className="mini-navbar" ref={miniNavRef} role="tablist" aria-label="Project category">
              <div
                className="mini-navbar-highlight"
                style={{ left: highlightStyle.left, width: highlightStyle.width }}
              />
              {PROJECT_SECTIONS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={section === tab.key}
                  className={`mini-navbar-tab${section === tab.key ? ' active' : ''}`}
                  onClick={() => setSection(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* `key` forces a remount per tab so the entry animation replays. */}
            <div className="projects-grid" key={section}>
              {section === 'web' && (
                <>
                  <article className="project-card reveal reveal-up">
                    <div className="project-media">
                      <div className="video-container">
                        <iframe
                          src="https://www.youtube.com/embed/GHky6jQOvuU?si=Ac4HxRMtCtfR8UQV"
                          title="Modern E-commerce Platform demo"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerPolicy="strict-origin-when-cross-origin"
                          allowFullScreen
                        />
                      </div>
                    </div>
                    <div className="project-info">
                      <div className="project-timestamp">
                        <time dateTime="2025-06-17">June 17, 2025</time>
                      </div>
                      <h2>Modern E-commerce Platform 2.0</h2>
                      <p>
                        A cutting-edge e-commerce solution built with modern JavaScript
                        frameworks and cloud technologies — advanced React frontend,
                        robust Express APIs, and a scalable PostgreSQL architecture.
                      </p>

                      <h3 className="project-subhead">Key features</h3>
                      <ul className="project-list">
                        <li>Modern React frontend with responsive design</li>
                        <li>RESTful API with Express.js backend</li>
                        <li>PostgreSQL database with advanced queries</li>
                        <li>Real-time inventory management</li>
                        <li>Secure payment gateway integration</li>
                        <li>Admin dashboard with analytics</li>
                      </ul>

                      <dl className="project-facts">
                        <div>
                          <dt>Build time</dt>
                          <dd>300 hours</dd>
                        </div>
                        <div>
                          <dt>Stack</dt>
                          <dd>React · Node · PostgreSQL</dd>
                        </div>
                      </dl>

                      <h3 className="project-subhead">Built with</h3>
                      <TechStack
                        items={[
                          { src: '/html.svg', alt: 'HTML', title: 'HTML5' },
                          { src: '/css.svg', alt: 'CSS', title: 'CSS3' },
                          { src: '/js.svg', alt: 'JavaScript', title: 'JavaScript' },
                          { src: '/react.svg', alt: 'React', title: 'React' },
                          { src: '/nodejs.svg', alt: 'Node.js', title: 'Node.js' },
                          { src: '/sql.svg', alt: 'PostgreSQL', title: 'PostgreSQL' },
                          { src: '/axios.svg', alt: 'Axios', title: 'Axios' },
                        ]}
                      />

                      <div className="project-cta">
                        <Link
                          to={`/contact?reason=quote&topic=${encodeURIComponent('Modern e-commerce / React platform')}`}
                          className="btn btn--primary btn--sm"
                        >
                          Get a site like this <span className="btn-arrow">→</span>
                        </Link>
                      </div>
                    </div>
                  </article>

                  <article className="project-card reveal reveal-up">
                    <div className="project-media">
                      <div className="video-container">
                        <iframe
                          src="https://www.youtube.com/embed/08xWrLK1HtA?si=hsBhLQ9-g5LGy8lL"
                          title="Full-stack e-commerce application demo"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerPolicy="strict-origin-when-cross-origin"
                          allowFullScreen
                        />
                      </div>
                    </div>
                    <div className="project-info">
                      <div className="project-timestamp">
                        <time dateTime="2020-06-15">June 15, 2020</time>
                      </div>
                      <h2>Full-Stack E-commerce Application</h2>
                      <p>
                        A complete e-commerce platform built from scratch — user
                        authentication, database management, and secure payment
                        processing, all production-ready.
                      </p>

                      <h3 className="project-subhead">Key features</h3>
                      <ul className="project-list">
                        <li>User authentication and account management</li>
                        <li>Shopping cart with multi-quantity purchases</li>
                        <li>Profile customization and settings</li>
                      </ul>

                      <h3 className="project-subhead">Built with</h3>
                      <TechStack
                        items={[
                          { src: '/html.svg', alt: 'HTML', title: 'HTML5' },
                          { src: '/css.svg', alt: 'CSS', title: 'CSS3' },
                          { src: '/js.svg', alt: 'JavaScript', title: 'JavaScript' },
                          { src: '/php.svg', alt: 'PHP', title: 'PHP' },
                          { src: '/sql.svg', alt: 'MySQL', title: 'MySQL' },
                        ]}
                      />

                      <p className="project-note">
                        <strong>Note</strong> Demonstrates full-stack development with
                        PHP and MySQL, including secure user authentication and
                        end-to-end e-commerce functionality.
                      </p>

                      <div className="project-cta">
                        <Link
                          to={`/contact?reason=quote&topic=${encodeURIComponent('E-commerce website similar to this demo')}`}
                          className="btn btn--primary btn--sm"
                        >
                          Get a similar site <span className="btn-arrow">→</span>
                        </Link>
                      </div>
                    </div>
                  </article>

                  {/* Pointer to the dedicated live-apps page */}
                  <article className="project-card project-card--pointer reveal reveal-scale">
                    <div className="project-pointer">
                      <span className="project-pointer-icon">
                        <IconArrowRight />
                      </span>
                      <div>
                        <h3>Looking for our live, in-production apps?</h3>
                        <p>
                          InvestNYou, Lurie Cup, ProScape, DocReplay and more each have
                          a live preview on the Live Apps page.
                        </p>
                      </div>
                      <Link to="/live-apps" className="btn btn--ghost btn--sm">
                        See live apps <span className="btn-arrow">→</span>
                      </Link>
                    </div>
                  </article>

                  <MoreToComeCard />
                </>
              )}

              {section === 'graphics' && (
                <>
                  <GraphicsCarouselProjectCard />
                  <MoreToComeCard />
                </>
              )}

              {section === 'video' && (
                <>
                  <VideoCarouselProjectCard />
                  <MoreToComeCard />
                </>
              )}
            </div>
          </div>
        </section>

        <section className="section section--alt projects-cta">
          <div className="container container--narrow projects-cta-inner">
            <p className="eyebrow eyebrow--plain">Next</p>
            <h2 className="section-title reveal reveal-up">Ready to see your project here?</h2>
            <p className="section-lede reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
              Tell us what you have in mind and we&rsquo;ll scope it honestly — concept
              through launch, or one piece of it.
            </p>
            <div className="btn-row reveal reveal-up" style={{ '--reveal-delay': '0.16s' }}>
              <Link to="/contact?reason=quote" className="btn btn--primary btn--lg">
                Start the conversation <span className="btn-arrow">→</span>
              </Link>
              <Link to="/products" className="btn btn--ghost btn--lg">
                See pricing
              </Link>
            </div>
            <p className="projects-cta-note">
              Free consultation · No commitment required
            </p>
          </div>
        </section>
      </main>
    </>
  );
};

/* -------------------------------------------------------------------------
   Graphics tab
   ------------------------------------------------------------------------- */

const GraphicsCarouselProjectCard = () => {
  const images = [
    { src: '/favicon.png', title: 'Logo Design' },
    { src: '/LogoSM.png', title: 'Logo Mark' },
    { src: '/IG.png', title: 'Instagram Post' },
    { src: '/SMCTP.png', title: 'Type & Palette' },
    { src: '/flyer.png', title: 'Flyer Design' },
    { src: '/1.png', title: 'Card Front' },
    { src: '/2.png', title: 'Card Back' },
    { src: '/SMTnG.png', title: 'IG Template' },
  ];
  const [current, setCurrent] = useState(0);

  return (
    <article className="project-card reveal reveal-up">
      <div className="project-media">
        <div className="graphic-container zoom-on-hover">
          <img key={images[current].src} src={images[current].src} alt={images[current].title} />
        </div>
        <CarouselControls
          items={images}
          current={current}
          onSelect={setCurrent}
          label="graphic"
        />
      </div>

      <div className="project-info">
        <div className="project-timestamp">
          <time dateTime="2023-08-20">August 20, 2023</time>
        </div>
        <h2>Brand Identity Design Package</h2>
        <p>
          A complete brand identity for a local business: logo design, colour palette,
          typography system, and a full style guide — cohesive visual identity that
          strengthens recognition across every surface.
        </p>

        <h3 className="project-subhead">Key deliverables</h3>
        <ul className="project-list">
          <li>Primary and secondary logo variations</li>
          <li>Brand colour palette and typography system</li>
          <li>Business card and letterhead designs</li>
          <li>Social media templates and guidelines</li>
          <li>Digital and print-ready assets</li>
        </ul>

        <h3 className="project-subhead">Built with</h3>
        <TechStack
          items={[
            { src: '/pixlr.svg', alt: 'Pixlr', title: 'Pixlr' },
            { src: '/canva.svg', alt: 'Canva', title: 'Canva' },
          ]}
        />

        <p className="project-note">
          <strong>Note</strong> Shows our graphic design range and the attention to
          detail that helps small businesses hold their own in competitive markets.
        </p>

        <div className="project-cta">
          <Link
            to={`/contact?reason=quote&topic=${encodeURIComponent('Brand identity / graphic design package')}`}
            className="btn btn--primary btn--sm"
          >
            Start a brand package <span className="btn-arrow">→</span>
          </Link>
        </div>
      </div>
    </article>
  );
};

/* -------------------------------------------------------------------------
   Video tab
   ------------------------------------------------------------------------- */

const VideoCarouselProjectCard = () => {
  const videos = [
    { src: '/media.mp4', title: 'Media Project' },
    { src: '/Senior.mp4', title: 'Senior Project' },
    { src: '/timeline.mp4', title: 'Timeline Project' },
    { src: '/PROMO.mp4', title: 'Promo Project' },
  ];
  const [current, setCurrent] = useState(0);

  return (
    <article className="project-card reveal reveal-up">
      <div className="project-media">
        <div className="video-container video-container--player">
          <video key={videos[current].src} src={videos[current].src} controls poster="/LogoSM.png">
            Sorry, your browser does not support embedded videos.
          </video>
        </div>
        <CarouselControls items={videos} current={current} onSelect={setCurrent} label="video" />
      </div>

      <div className="project-info">
        <div className="project-timestamp">Created over the course of a year</div>
        <h2>Video Editing Gallery — Gorilla Bots</h2>
        <p>
          Featured editing work for Gorilla Bots, a local First Tech Challenge team.
          Event highlights, creative edits, and promo pieces — use the arrows or
          thumbnails to move through the reel.
        </p>

        <h3 className="project-subhead">Project scope</h3>
        <ul className="project-list">
          <li>Multi-camera editing and colour grading</li>
          <li>Motion graphics and transitions</li>
          <li>Sound design and audio mixing</li>
          <li>Export for web and social platforms</li>
        </ul>

        <h3 className="project-subhead">Built with</h3>
        <TechStack items={[{ src: '/davinci.png', alt: 'DaVinci Resolve', title: 'DaVinci Resolve' }]} />

        <p className="project-note">
          <strong>Note</strong> This reel shows our range across post-production. A few
          of these cuts are still in progress.
        </p>

        <div className="project-cta">
          <Link
            to={`/contact?reason=quote&topic=${encodeURIComponent('Video editing / post-production')}`}
            className="btn btn--primary btn--sm"
          >
            Start a video project <span className="btn-arrow">→</span>
          </Link>
        </div>
      </div>
    </article>
  );
};

export default Projects;
